import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  FiShield as Shield,
  FiDollarSign as DollarSign,
  FiCalendar as Calendar,
  FiPhone as Phone,
  FiUsers as Users,
  FiSettings as Settings,
  FiBell as Bell
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { currentPolicies } from '../utils/policyStatus';
import AccountLinkingNotice from './AccountLinkingNotice';
import PolicyCard from './PolicyCard';
import RenewalNotifications from './RenewalNotifications';
import ExpirationAlertsWidget from './ExpirationAlertsWidget';

const DashboardHome = () => {
  const { user, ensureProfile } = useAuth();
  const navigate = useNavigate();
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);

  useEffect(() => {
    ensureProfile();
  }, []);

  const timeGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.firstName?.trim() || '';
  const greeting = firstName ? `${timeGreeting()}, ${firstName}` : timeGreeting();

  // Calculate renewal notifications count
  const renewalCount = user?.policies?.filter(policy => {
    const today = new Date();
    const expDate = new Date(policy.expirationDate);
    const daysUntilExpiry = Math.floor((expDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 90;
  }).length || 0;

  // F1 returns expired rows too, so "active" has to be computed, not counted.
  const activePolicies = currentPolicies(user?.policies);

  const summaryCards = [
    {
      title: 'Active Policies',
      value: activePolicies.length,
      icon: Shield,
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Next Renewal',
      value: getNextRenewalDate(user?.policies || []),
      icon: Calendar,
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      subtitle: getNextRenewalPolicy(user?.policies || [])
    }
  ];

  // Signed in, but not yet matched to a client record. Show one honest
  // explanation instead of the whole dashboard rendered as zeroes -- "0 Active
  // Policies" and "None on file" read as "you have no coverage", which is a
  // claim this app is in no position to make about someone it cannot identify.
  if (!user?.clientId) {
    return (
      <div className="space-y-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{greeting}! 👋</h1>
          <p className="text-gray-600">Welcome to your insurance dashboard.</p>
        </div>
        <AccountLinkingNotice />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header with Renewal Alert */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {greeting}! 👋
            </h1>
            <p className="text-gray-600">
              Welcome to your insurance dashboard. Here's an overview of your coverage.
            </p>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {renewalCount > 0 && (
              <button
                onClick={() => setShowRenewalModal(true)}
                className="relative bg-red-50 text-red-700 px-4 py-3 rounded-lg font-semibold hover:bg-red-100 transition-colors flex items-center gap-2 border border-red-200"
              >
                <Bell size={20} />
                <span>{renewalCount} Renewal{renewalCount !== 1 ? 's' : ''}</span>
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center animate-pulse">
                  {renewalCount}
                </span>
              </button>
            )}
            <div className="w-20 h-20 bg-gradient-to-br from-[#DC143C] to-[#B01030] rounded-full flex items-center justify-center">
              <Shield size={32} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Renewal Alert */}
      {renewalCount > 0 && (
        <div className="md:hidden bg-red-50 border border-red-200 rounded-xl p-4">
          <button
            onClick={() => setShowRenewalModal(true)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-red-600" />
              <div className="text-left">
                <div className="font-semibold text-red-900">
                  {renewalCount} Policy Renewal{renewalCount !== 1 ? 's' : ''} Due
                </div>
                <div className="text-sm text-red-700">Tap to view details</div>
              </div>
            </div>
            <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
              View
            </span>
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {summaryCards.map((card, index) => (
          <div
            key={index}
            className={`${card.bgColor} p-6 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${card.bgColor} rounded-lg flex items-center justify-center`}>
                <card.icon size={24} className={card.iconColor} />
              </div>
            </div>

            <div className="text-2xl font-bold text-gray-900 mb-1">
              {card.value}
            </div>
            <div className="text-sm text-gray-600">
              {card.title}
            </div>
            {card.subtitle && (
              <div className="text-xs text-gray-500 mt-1">
                {card.subtitle}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Expiration Alerts Widget */}
      <ExpirationAlertsWidget 
        isCompact={true} 
        onViewAll={() => setShowAlertsModal(true)}
      />

      {/* Your Policies Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Your Policies</h2>
          <button
            onClick={() => navigate('/dashboard/policies')}
            className="text-[#DC143C] font-semibold hover:underline"
          >
            View All
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {user?.policies?.slice(0, 4).map((policy) => (
            <PolicyCard key={policy.id} policy={policy} />
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: DollarSign, label: 'Make Payment', color: 'red', to: '/dashboard/payment' },
          { icon: Phone, label: 'File Claim', color: 'blue', to: '/dashboard/claims' },
          { icon: Users, label: 'Refer Friend', color: 'green', to: '/dashboard/referrals' },
          { icon: Settings, label: 'Settings', color: 'gray', to: '/dashboard/settings' }
        ].map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.to)}
            className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200 text-left group"
          >
            <action.icon
              size={24}
              className={`text-${action.color}-600 mb-2 group-hover:scale-110 transition-transform`}
            />
            <div className="font-medium text-gray-900">{action.label}</div>
          </button>
        ))}
      </div>

      {/* Renewal Modal */}
      {showRenewalModal && (
        <RenewalNotifications
          onClose={() => setShowRenewalModal(false)}
          isWidget={false}
        />
      )}

      {/* Alerts Modal */}
      {showAlertsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Policy Expiration Alerts</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Stay informed about upcoming policy renewals
                </p>
              </div>
              <button
                onClick={() => setShowAlertsModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Bell size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <ExpirationAlertsWidget isCompact={false} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// A renewal is a future event. Sorting every returned policy by expiration and
// taking the first one surfaced the OLDEST expired policy -- the walk account
// was told "Next Renewal: Jan 16, 2009". Only current policies can renew.
function nextRenewing(policies) {
  return currentPolicies(policies)
    .map(p => ({ ...p, expDate: new Date(p.expirationDate) }))
    .sort((a, b) => a.expDate - b.expDate)[0] || null;
}

function getNextRenewalDate(policies) {
  const next = nextRenewing(policies);
  if (!next) return 'None on file';
  return format(next.expDate, 'MMM dd, yyyy');
}

function getNextRenewalPolicy(policies) {
  return nextRenewing(policies)?.policyType || '';
}

export default DashboardHome;